// Regla de bloqueo presupuestal por umbral (feature flag `budget_hard_block`).
//
// Requisito: "bloqueo en la prestación del servicio una vez se llegue al 98%
// de la ejecución presupuestal de cada unidad ejecutora". La ejecución se mide
// sobre el fondo completo (spent / amount), NO sobre el disponible restante,
// que era lo que mostraba el formulario y por eso el tope nunca se veía.
//
// El backend (`assertBudgetThreshold`) es la fuente de verdad; esto replica
// la regla en el cliente para que el usuario vea el tope antes de enviar y
// el botón quede bloqueado. Toda la aritmética es en centavos para evitar
// falsos positivos por flotantes.

export const DEFAULT_THRESHOLD = 0.98;

const toCents = (v) => Math.round((parseFloat(v) || 0) * 100);

/**
 * Lee la configuración del flag desde `state.features`.
 * @returns {{ enabled: boolean, threshold: number }}
 */
export function hardBlockConfig(features) {
    const cfg = features && features.budget_hard_block;
    if (!cfg || !cfg.enabled) return { enabled: false, threshold: 1 };
    const t = Number(cfg.threshold);
    return { enabled: true, threshold: t > 0 && t <= 1 ? t : DEFAULT_THRESHOLD };
}

/**
 * Snapshot del fondo al momento de cargar el formulario.
 * `originalOrderTotal` es lo que este mismo pedido ya tiene ejecutado en el
 * fondo (0 al crear; total persistido al editar) para no contarlo dos veces.
 */
export function buildFundSnapshot(fund, originalOrderTotal = 0) {
    if (!fund) return null;
    const amount = parseFloat(fund.amount);
    const spent = parseFloat(fund.spent);
    const available = parseFloat(fund.available);
    const hasLedger = Number.isFinite(amount) && amount > 0 && Number.isFinite(spent);
    return {
        id: fund.id,
        // Si la API no expone amount/spent se degrada al comportamiento previo
        // (sólo disponible): el % de ejecución no será exacto pero no rompe.
        amount: hasLedger ? amount : (Number.isFinite(available) ? available + originalOrderTotal : 0),
        baseSpent: hasLedger ? Math.max(0, spent - originalOrderTotal) : 0,
        hasLedger,
    };
}

/**
 * Evalúa el pedido contra el fondo.
 * @param {ReturnType<typeof buildFundSnapshot>} snap
 * @param {number} orderTotal   total actual del pedido en pantalla
 * @param {{enabled:boolean, threshold:number}} cfg
 */
export function evaluateBudget(snap, orderTotal, cfg) {
    if (!snap || !(snap.amount > 0)) return null;

    const amountC = toCents(snap.amount);
    const baseSpentC = toCents(snap.baseSpent);
    const orderC = toCents(orderTotal);
    const projectedC = baseSpentC + orderC;

    // Con el flag apagado el tope es el 100% (comportamiento histórico).
    const thresholdPct = cfg.enabled ? cfg.threshold * 100 : 100;
    const capC = Math.floor(amountC * (cfg.enabled ? cfg.threshold : 1));

    const pct = (c) => (c / amountC) * 100;
    const currentPct = pct(baseSpentC);
    const projectedPct = pct(projectedC);

    // "una vez se llegue al X%": un fondo que ya está en el tope no admite
    // más pedidos (alreadyBlocked), y ningún pedido puede llevarlo por encima
    // del tope. Un pedido que lo deja exactamente en el tope es el último que
    // entra. Con el flag apagado sólo se rechaza superar el 100% (histórico).
    // Si el backend resultara más estricto, ajustar aquí; el cliente puede ser
    // más estricto que el servidor, nunca más laxo.
    const alreadyBlocked = cfg.enabled && baseSpentC >= capC;
    const blocked = alreadyBlocked || projectedC > capC;
    const roomC = Math.max(0, capC - baseSpentC);        // máximo que este pedido puede valer
    const excessC = Math.max(0, projectedC - capC);

    return {
        enabled: cfg.enabled,
        thresholdPct,
        amount: amountC / 100,
        baseSpent: baseSpentC / 100,
        projectedSpent: projectedC / 100,
        cap: capC / 100,
        currentPct,
        projectedPct,
        roomForOrder: roomC / 100,
        remainingAfterOrder: Math.max(0, (roomC - orderC)) / 100,
        excess: excessC / 100,
        blocked,
        alreadyBlocked,
    };
}

/** Detecta si un mensaje de error de la API corresponde al rechazo por umbral. */
export function isThresholdError(msg) {
    return /threshold|budget|presupuest|umbral|exceed|superad/i.test(String(msg || ''));
}
